import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../lib/auth/AuthProvider';
import { askAssistant, type AssistantAction, type AssistantTurn } from '../lib/assistant/assistantApi';
import { useDictation, useSpokenAnswer } from '../lib/assistant/useSpeech';
import { getMyVault } from '../lib/vault/vaultApi';
import './AssistantPanel.css';

/**
 * The in-app guide.
 *
 * Written for the person this product is actually for: often in their
 * seventies, often on a phone, often not confident that they are doing it
 * right. So the control says "Help" in words rather than being a lone icon,
 * the type is large, and the panel opens with three real questions already
 * written out — someone who is unsure what to ask can simply tap one.
 *
 * The panel sends the screen name and three counts, nothing else. It never
 * reads a message or a recipient name, so there is nothing here that could
 * leak one.
 */

/** Route → the name a person would use for the screen they are looking at. */
function screenLabel(pathname: string): string {
  if (pathname === '/') return 'Vault';
  if (pathname.startsWith('/messages')) return 'Messages';
  if (pathname.startsWith('/record')) return 'Record a message';
  if (pathname.startsWith('/guardians')) return 'Guardians';
  if (pathname.startsWith('/executors')) return 'Executor';
  if (pathname.startsWith('/recipients')) return 'Recipients';
  if (pathname.startsWith('/replies')) return 'Replies';
  if (pathname.startsWith('/you')) return 'You';
  return 'Legacy Architect';
}

/** Openers chosen per screen: the question someone is most likely stuck on there. */
function suggestionsFor(screen: string): string[] {
  switch (screen) {
    case 'Record a message':
      return [
        'What should I say in my first message?',
        'Can I record it again if I get it wrong?',
        'What is the difference between a video and a letter?',
      ];
    case 'Messages':
      return [
        'Can I change a message after I have sealed it?',
        'How do I choose when a message is delivered?',
        'What happens if my person changes their email?',
      ];
    case 'Guardians':
      return [
        'What does a guardian actually do?',
        'Can my guardian read my messages?',
        'Who should I choose as a guardian?',
      ];
    default:
      return [
        'What is this and what should I do first?',
        'Can anyone else read what I record?',
        'What happens if I lose my phone?',
      ];
  }
}

export function AssistantPanel() {
  const { api } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const screen = screenLabel(pathname);

  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [turns, setTurns] = useState<AssistantTurn[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vault, setVault] = useState<
    { hasVault: boolean; recipientCount: number; sealedMessageCount: number } | null
  >(null);

  const { speak, stop: stopSpeaking, speakingIndex, available: voiceAvailable, setAvailable: setVoiceAvailable } =
    useSpokenAnswer(api);
  const dictation = useDictation((text) => setQuestion((q) => (q === '' ? text : `${q} ${text}`)));

  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const logRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLButtonElement | null>(null);

  // Counts are fetched once, on first open — not on every screen — so a member
  // who never asks for help pays nothing for the widget being mounted.
  useEffect(() => {
    if (!open || vault !== null) return;
    let live = true;
    getMyVault(api)
      .then((v) => {
        if (live) {
          setVault({
            hasVault: true,
            recipientCount: v.aggregates.recipient_count,
            sealedMessageCount: v.aggregates.sealed_count,
          });
        }
      })
      .catch(() => {
        // A member whose vault is not open yet 404s here. Recording that as
        // hasVault:false matters: it stops the assistant offering to take
        // them to "Add someone", a screen that would bounce them straight
        // back. An assistant that sends people to doors that are locked is
        // worse than one that says nothing.
        if (live) setVault({ hasVault: false, recipientCount: 0, sealedMessageCount: 0 });
      });
    return () => {
      live = false;
    };
  }, [open, vault, api]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
    else {
      stopSpeaking();
      openerRef.current?.focus();
    }
  }, [open, stopSpeaking]);

  // Escape closes from anywhere. Bound to the panel element it only fired
  // while focus was still inside it, and pressing Ask disables the button,
  // which drops focus to the body — so the key did nothing at the exact
  // moment someone was most likely to reach for it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Keep the newest answer in view without yanking the page for someone who
  // has asked the browser to reduce motion.
  useEffect(() => {
    const el = logRef.current;
    if (!el) return;
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  }, [turns, busy]);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (trimmed === '' || busy) return;
      setError(null);
      setBusy(true);
      const history = turns;
      setTurns([...history, { role: 'user', text: trimmed }]);
      setQuestion('');
      try {
        const { answer, action, voiceAvailable: hasVoice } = await askAssistant(
          api,
          trimmed,
          history.map((h) => ({ role: h.role, text: h.text })),
          {
            screen,
            hasVault: vault?.hasVault ?? false,
            recipientCount: vault?.recipientCount,
            sealedMessageCount: vault?.sealedMessageCount,
          },
        );
        if (hasVoice !== undefined) setVoiceAvailable(hasVoice);
        setTurns((prev) => [...prev, { role: 'assistant', text: answer, action }]);
      } catch {
        setError('I could not reach the assistant just then. Please try again in a moment.');
      } finally {
        setBusy(false);
      }
    },
    [api, busy, vault, screen, turns, setVoiceAvailable],
  );

  if (!open) {
    return (
      <button
        ref={openerRef}
        type="button"
        className="assistant__opener"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
      >
        <span aria-hidden="true">?</span> Help
      </button>
    );
  }

  return (
    <section
      className="assistant"
      role="dialog"
      aria-modal="false"
      aria-label="Help"
    >
      <header className="assistant__head">
        <h2 className="assistant__title">Ask anything</h2>
        <button type="button" className="assistant__close" onClick={() => setOpen(false)}>
          Close
        </button>
      </header>

      <div className="assistant__log" ref={logRef} aria-live="polite" aria-busy={busy}>
        {turns.length === 0 && (
          <>
            <p className="assistant__intro">
              I can explain anything about Legacy Architect — how recording works, who can see what, what
              happens next. I cannot see inside your vault.
            </p>
            <ul className="assistant__suggestions">
              {suggestionsFor(screen).map((s) => (
                <li key={s}>
                  <button type="button" onClick={() => void send(s)}>
                    {s}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}

        {turns.map((t, i) =>
          t.role === 'user' ? (
            <p key={i} className="assistant__you">
              {t.text}
            </p>
          ) : (
            <div key={i} className="assistant__replyBlock">
              <p className="assistant__reply">{t.text}</p>
              <div className="assistant__replyActions">
                {voiceAvailable && (
                  <button
                    type="button"
                    className="assistant__listen"
                    onClick={() => void speak(i, t.text)}
                    aria-label={speakingIndex === i ? 'Stop reading aloud' : 'Read this answer aloud'}
                  >
                    {speakingIndex === i ? 'Stop' : 'Listen'}
                  </button>
                )}
                {t.action !== undefined && (
                  <GoThereButton action={t.action} onGo={(path) => {
                    stopSpeaking();
                    setOpen(false);
                    navigate(path);
                  }} />
                )}
              </div>
            </div>
          ),
        )}

        {busy && <p className="assistant__thinking">Thinking…</p>}
        {error !== null && <p className="assistant__error">{error}</p>}
      </div>

      <form
        className="assistant__form"
        onSubmit={(e) => {
          e.preventDefault();
          void send(question);
        }}
      >
        <label className="assistant__label" htmlFor="assistant-question">
          Your question
        </label>
        <textarea
          id="assistant-question"
          ref={inputRef}
          className="assistant__input"
          rows={2}
          value={question}
          maxLength={2000}
          placeholder="Type your question here"
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends; Shift+Enter makes a new line. On a phone the return
            // key is the obvious way to finish, and hunting for a button is not.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send(question);
            }
          }}
        />
        <div className="assistant__actions">
          {dictation.supported && (
            <button
              type="button"
              className={dictation.listening ? 'assistant__mic assistant__mic--live' : 'assistant__mic'}
              onClick={dictation.toggle}
              aria-label={dictation.listening ? 'Stop speaking' : 'Speak your question instead of typing'}
            >
              {dictation.listening ? 'Listening…' : 'Speak'}
            </button>
          )}
          <button type="submit" className="assistant__send" disabled={busy || question.trim() === ''}>
            {busy ? 'Asking…' : 'Ask'}
          </button>
        </div>
      </form>
    </section>
  );
}

/**
 * The one place the assistant touches the app itself. It is a button, not an
 * automatic jump: the member reads what the assistant said, then decides.
 */
function GoThereButton({ action, onGo }: { action: AssistantAction; onGo: (path: string) => void }) {
  return (
    <button type="button" className="assistant__goto" onClick={() => onGo(action.path)}>
      {action.label}
    </button>
  );
}
